import React, { useEffect, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useUIStore } from './store/uiStore';
import './services/i18n'; // Initialize i18n
import { useAutoSave } from './hooks/useAutoSave';

// Screen Mapping
import { SCREEN_MAPPING } from './config/screenMapping';

// Embedded App Provider
import EmbeddedAppProvider, { useEmbeddedApp } from './providers/EmbeddedAppProvider';
import ContextLoadingScreen from './components/ContextLoadingScreen';
import OfflineBanner from './components/OfflineBanner';
import NotificationContainer from './components/NotificationContainer';
import ClaimNotEditableModal from './components/ClaimNotEditableModal';
import LoadingSpinner from './components/LoadingSpinner';
import TestMapperButton from './components/TestMapperButton';

// Core screens (loaded immediately - needed for initial load)
import HomeScreen from './screens/HomeScreen';
import ClaimDetailsScreen from './screens/ClaimDetailsScreen';
import PrepareClaimScreen from './screens/PrepareClaimScreen';
import EventSelectionScreen from './screens/EventSelectionScreen';
import LegalNoticeScreen from './screens/LegalNoticeScreen';
import ConfirmationScreen from './screens/ConfirmationScreen';

// Maintenance page (DEV-6343) — eager import so it renders even while the BFF is unavailable
import MaintenancePage from './screens/MaintenancePage';

// Common screens (loaded on-demand but shared across flows)
const AutomatedConsentScreen = lazy(() => import('./screens/AutomatedConsentScreen'));
const ContractSelectionScreen = lazy(() => import('./screens/ContractSelectionScreen'));
const OtherInsuranceScreen = lazy(() => import('./screens/OtherInsuranceScreen'));
const TripPaymentMethodScreen = lazy(() => import('./screens/TripPaymentMethodScreen'));
const ClaimTypeScreen = lazy(() => import('./screens/ClaimTypeScreen'));
const InvoiceUploadScreen = lazy(() => import('./screens/InvoiceUploadScreen'));
const ExpenseValidationScreen = lazy(() => import('./screens/ExpenseValidationScreen'));
const SupportingDocumentsScreen = lazy(() => import('./screens/SupportingDocumentsScreen'));
const EligibilityAnalysisScreen = lazy(() => import('./screens/EligibilityAnalysisScreen'));
const BankingInfoScreen = lazy(() => import('./screens/BankingInfoScreen'));
const MandateSignatureScreen = lazy(() => import('./screens/MandateSignatureScreen'));
const ContactPreferencesScreen = lazy(() => import('./screens/ContactPreferencesScreen'));
const EventDateScreen = lazy(() => import('./screens/EventDateScreen'));

// Medical Flow Screens (lazy-loaded as a chunk)
const BeneficiaryScreen = lazy(() => import('./screens/medical/BeneficiaryScreen'));
const TravelConfirmationScreen = lazy(() => import('./screens/medical/TravelConfirmationScreen'));
const SpecificClaimTypeScreen = lazy(() => import('./screens/medical/SpecificClaimTypeScreen'));
const SpecificVerificationsScreen = lazy(() => import('./screens/medical/SpecificVerificationsScreen'));
const EventDetailsScreen = lazy(() => import('./screens/medical/EventDetailsScreen'));
const MedicalVerificationScreen = lazy(() => import('./screens/medical/MedicalVerificationScreen'));
const ReimbursementDetailsScreen = lazy(() => import('./screens/medical/ReimbursementDetailsScreen'));

// Cancellation Flow Screens (lazy-loaded as a chunk)
const BeneficiaryScreenCancellation = lazy(() => import('./screens/cancellation/BeneficiaryScreenCancellation'));
const TravelCancellationScreen = lazy(() => import('./screens/cancellation/TravelCancellationScreen'));
const CancellationDetailsScreen1 = lazy(() => import('./screens/cancellation/CancellationDetailsScreen1'));
const CancellationDetailsScreen2 = lazy(() => import('./screens/cancellation/CancellationDetailsScreen2'));
const CancellationDetailsScreen3 = lazy(() => import('./screens/cancellation/CancellationDetailsScreen3'));
const CancellationDetailsScreen4 = lazy(() => import('./screens/cancellation/CancellationDetailsScreen4'));
const CancellationDetailsScreen5 = lazy(() => import('./screens/cancellation/CancellationDetailsScreen5'));
const CancellationMedicalVerificationScreen = lazy(() => import('./screens/cancellation/CancellationMedicalVerificationScreen'));
const CancellationCategoryScreen = lazy(() => import('./screens/cancellation/CancellationCategoryScreen'));

// Baggage Flow Screens (lazy-loaded as a chunk)
const BeneficiaryScreenBaggage = lazy(() => import('./screens/baggage/BeneficiaryScreenBaggage'));
const TravelConfirmationScreenBaggage = lazy(() => import('./screens/baggage/TravelConfirmationScreenBaggage'));
const BaggageDetailsScreen = lazy(() => import('./screens/baggage/BaggageDetailsScreen'));
const BaggageVerificationsScreen = lazy(() => import('./screens/baggage/BaggageVerificationsScreen'));
const BaggageCategoryScreen = lazy(() => import('./screens/baggage/BaggageCategoryScreen'));

// Append Expenses Flow Screens (DEV-6623)
const NewExpensesIntroScreen = lazy(() => import('./screens/append-expenses/NewExpensesIntroScreen'));
const AppendReimbursementDetailsScreen = lazy(() => import('./screens/append-expenses/AppendReimbursementDetailsScreen'));
const AppendInvoiceUploadScreen = lazy(() => import('./screens/append-expenses/AppendInvoiceUploadScreen'));
const AppendExpenseValidationScreen = lazy(() => import('./screens/append-expenses/AppendExpenseValidationScreen'));
const AppendSupportingDocumentsScreen = lazy(() => import('./screens/append-expenses/AppendSupportingDocumentsScreen'));

// Append Documents Flow Screens (append-document)
const AppendNewDocumentsScreen = lazy(() => import('./screens/append-documents/AppendNewDocumentsScreen'));

// Component mapping for dynamic routing
const componentMap: Record<string, React.ComponentType> = {
  HomeScreen,
  ClaimDetailsScreen,
  PrepareClaimScreen,
  EventSelectionScreen,
  LegalNoticeScreen,
  AutomatedConsentScreen,
  EventDateScreen,
  ContactPreferencesScreen,
  ContractSelectionScreen,
  OtherInsuranceScreen,
  TripPaymentMethodScreen,
  BeneficiaryScreen,
  TravelConfirmationScreen,
  ClaimTypeScreen,
  SpecificClaimTypeScreen,
  SpecificVerificationsScreen,
  EventDetailsScreen,
  MedicalVerificationScreen,
  ReimbursementDetailsScreen,
  InvoiceUploadScreen,
  ExpenseValidationScreen,
  SupportingDocumentsScreen,
  EligibilityAnalysisScreen,
  BankingInfoScreen,
  MandateSignatureScreen,
  ConfirmationScreen,
  // Medical Flow Screens
  BeneficiaryScreenMedical: BeneficiaryScreen, // Reuse existing for now
  TravelConfirmationScreenMedical: TravelConfirmationScreen, // Reuse existing for now

  // Cancellation Flow Screens
  BeneficiaryScreenCancellation,
  TravelCancellationScreen,
  CancellationDetailsScreen1,
  CancellationDetailsScreen2,
  CancellationDetailsScreen3,
  CancellationDetailsScreen4,
  CancellationDetailsScreen5,
  CancellationMedicalVerificationScreen,
  CancellationCategoryScreen,

  // Baggage Flow Screens
  BeneficiaryScreenBaggage,
  TravelConfirmationScreenBaggage,
  BaggageDetailsScreen,
  BaggageVerificationsScreen,
  BaggageCategoryScreen,

  // Append Expenses Flow (DEV-6623)
  NewExpensesIntroScreen,
  AppendReimbursementDetailsScreen,
  AppendInvoiceUploadScreen,
  AppendExpenseValidationScreen,
  AppendSupportingDocumentsScreen,

  // Append Documents Flow (append-document)
  AppendNewDocumentsScreen
};


// Embedded App Wrapper - handles context loading and initialization
const EmbeddedAppWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isLoading, error, requestRefresh } = useEmbeddedApp();
  const maintenance = useUIStore((state) => state.maintenance);

  // Maintenance gate (DEV-6343): takes precedence over loading/error. The maintenance flag is set by
  // the bootstrap probe (run before extract-context in EmbeddedAppProvider) or by the axios 503
  // interceptor, so the maintenance page shows even if the handshake was short-circuited or failed.
  if (maintenance) {
    return <MaintenancePage />;
  }

  // Show loading screen while waiting for parent context
  if (isLoading || error) {
    return (
      <ContextLoadingScreen
        isLoading={isLoading}
        error={error}
        isEmbedded={true}
        onRetry={requestRefresh}
      />
    );
  }

  // Render app once context is loaded
  return <>{children}</>;
};

// Route change notifier - sends current route to parent
const RouteChangeNotifier: React.FC = () => {
  const location = useLocation();
  const { sendMessageToParent } = useEmbeddedApp();

  useEffect(() => {
    // Notify parent of route change
    sendMessageToParent({
      type: 'ROUTE_CHANGED',
      route: location.pathname + location.search + location.hash
    });
  }, [location, sendMessageToParent]);

  return null;
};

// Main App Component with embedded architecture
const AppContent: React.FC = () => {
  const { language, setLanguage } = useUIStore();
  const { parentContext, isEmbedded } = useEmbeddedApp();

  // Enable auto-save on stepData changes (debounced 2s with deep comparison).
  // The append-expenses flow (DEV-6623) is automatically skipped by the hook when a
  // referenceClaim is loaded — per spec, no draft Submission is created until the final
  // Submit on 04E-03-JUSTIFICATIFS.
  useAutoSave({ enabled: true, debounceMs: 2000 });

  // Initialize language on mount - sync i18n with zustand store
  useEffect(() => {
    import('./services/i18n').then(({ default: i18n }) => {
      // On mount, ensure i18n matches the zustand store language
      const lang = language.toLowerCase();
      if (i18n.language !== lang) {
        i18n.changeLanguage(lang);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount

  // Update language from parent context ONLY when parent context language changes
  // Do NOT react to manual language changes by the user
  useEffect(() => {
    if (parentContext?.userProfile?.language) {
      const parentLang = parentContext.userProfile.language;
      const currentLang = useUIStore.getState().language;

      // Only update if parent language actually changed (not if user changed it)
      if (parentLang !== currentLang) {
        setLanguage(parentLang);
      }
    }
    // IMPORTANT: Only depend on parent context language, NOT on local language state
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parentContext?.userProfile?.language]);

  // Generate dynamic routes from screen mapping
  const renderRoutes = () => {
    const routes: JSX.Element[] = [];

    // Add home route explicitly
    routes.push(<Route key="home" path="/" element={<HomeScreen />} />);

    // Add claim details view route (00-10-RECLAMATIONS)
    routes.push(<Route key="claim-details" path="/claim/00-10-RECLAMATIONS" element={<ClaimDetailsScreen />} />);

    // Generate routes from screen mapping
    Object.entries(SCREEN_MAPPING).forEach(([screenCode, config]) => {
      // Skip home screen as it's already added
      if (screenCode === '00-00-ACCUEIL') return;

      const Component = componentMap[config.component];
      if (Component) {
        routes.push(
          <Route
            key={screenCode}
            path={config.route}
            element={
              <Suspense fallback={<LoadingSpinner size="lg" text="Chargement..." className="min-h-screen" />}>
                <Component />
              </Suspense>
            }
          />
        );
      }
    });

    // Legacy routes for backward compatibility (will be removed later)
    // These redirect old step-based routes to new screen code routes
    const legacyRouteMap: Record<string, string> = {
      '/claim/step/1': '/claim/00-02-AVIS',
      '/claim/step/2': '/claim/01-00-DEMANDEUR',
      '/claim/step/3': '/claim/01-01-CONTRAT',
      '/claim/step/4': '/claim/01-02-AUTRES-CONTRATS',
      '/claim/step/5': '/claim/02M-01-BENEFICIAIRE',
      '/claim/step/6': '/claim/02M-02-VOYAGE',
      '/claim/step/7': '/claim/02-00-GARANTIE',
      '/claim/step/8': '/claim/02-00-GARANTIE', // SpecificClaimType - redirects to ClaimType for now
      '/claim/step/9': '/claim/03M-00-ACCIDENT',
      '/claim/step/10': '/claim/03M-01-DETAILS',
      '/claim/step/11': '/claim/03M-02-VERIFICATIONS',
      '/claim/step/12': '/claim/04M-00-REMBOURSEMENT',
      '/claim/step/13': '/claim/04-01-FACTURES',
      '/claim/step/14': '/claim/04-02-VALIDATION',
      '/claim/step/15': '/claim/04-03-JUSTIFICATIFS',
      '/claim/step/16': '/claim/05-00-ELIGIBILITE',
      '/claim/step/17': '/claim/05-01-PAIEMENT',
      '/claim/step/18': '/claim/05M-02-MANDAT',
      '/claim/step/19': '/claim/05-03-AUTORISATIONS',
      '/claim/step/20': '/claim/05-03-FIN'
    };

    Object.entries(legacyRouteMap).forEach(([oldPath, newPath]) => {
      routes.push(
        <Route
          key={`legacy-${oldPath}`}
          path={oldPath}
          element={<Navigate to={newPath} replace />}
        />
      );
    });

    // Redirect unknown routes to home
    routes.push(<Route key="fallback" path="*" element={<Navigate to="/" replace />} />);

    return routes;
  };

  return (
    <div className="App">
      {/* Only show banners/notifications in standalone mode, not when embedded */}
      {!isEmbedded && <OfflineBanner />}
      {!isEmbedded && <NotificationContainer />}
      <RouteChangeNotifier />
      {/* DEV-8836: draft abandoned/deleted from another tab — shown embedded or not */}
      <ClaimNotEditableModal />
      <Routes>
        {renderRoutes()}
      </Routes>
      {/* Dev Tools - Test Mapper Button (only in development mode) */}
      <TestMapperButton />
    </div>
  );
};

// Main App with Embedded Provider
const App: React.FC = () => {
  return (
    <EmbeddedAppProvider>
      <Router>
        <EmbeddedAppWrapper>
          <AppContent />
        </EmbeddedAppWrapper>
      </Router>
    </EmbeddedAppProvider>
  );
};

export default App;